// ============================================================================
// MIS TRADING SYSTEM - Frontend TypeScript Types
// ============================================================================

// ============================================================================
// ENUMS
// ============================================================================

export enum UserRole {
  SUPER_ADMIN = 'super_admin',  // faisalak056@outlook.com - Full access
  ADMIN = 'admin',              // murshid@percentinvestments.com - Full access
  HR = 'hr',                    // HR role - manages employees, salary, attendance only
  DEALER = 'dealer',            // Dealer — Dashboard / Trades / Market / Risk / Clients
  AGENT = 'agent',              // Legacy alias of DEALER
  CLIENT = 'client',            // Legacy - for trading clients
  EMPLOYEE = 'employee',        // @percentinvestments.com employees - View own payslips only
}

// Special email addresses
export const SUPER_ADMIN_EMAIL = 'faisalak056@outlook.com';
export const ADMIN_EMAIL = 'murshid@percentinvestments.com';
export const COMPANY_EMAIL_DOMAIN = '@percentinvestments.com';

export enum TradeType {
  STOCK = 'Stock',             // Legacy → EQUITY
  OPTION = 'Option',
  INTRA_STOCK = 'Intra Stock', // Legacy → EQUITY INTRADAY
  EQUITY = 'Equity',
  FUTURE = 'Future',
}

export enum TradeStatus {
  OPEN = 'OPEN',
  CLOSED = 'CLOSED',
}

export enum AlertSeverity {
  LOW = 'low',
  MEDIUM = 'medium',
  HIGH = 'high',
  CRITICAL = 'critical',
}

// ============================================================================
// USER TYPES
// ============================================================================

export interface User {
  userId: string;
  email: string;
  name: string;
  roles: UserRole[];          // Multiple roles support
  role?: UserRole;            // Deprecated - primary role for backwards compatibility
  clientId?: string;
  assignedClientIds?: string[];
  employeeId?: string;        // Links to Employee record for EMPLOYEE role
  isActive: boolean;
  isVerified: boolean;        // Email verified via OTP
  createdAt: string;
  updatedAt: string;
}

// OTP related types
export interface SendOTPInput {
  email: string;
  purpose: 'signup' | 'login' | 'reset_password';
}

export interface VerifyOTPInput {
  email: string;
  otp: string;
  purpose: 'signup' | 'login' | 'reset_password';
}

export interface SignupWithOTPInput {
  email: string;
  otp: string;
  name: string;
}

export interface AuthTokens {
  accessToken: string;
  refreshToken?: string;
  expiresIn: number;
}

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface AuthState {
  user: User | null;
  tokens: AuthTokens | null;
  isAuthenticated: boolean;
  isLoading: boolean;
}

// ============================================================================
// CLIENT TYPES
// ============================================================================

export interface BrokerageRates {
  stock: number;       // Equity delivery brokerage rate (decimal, e.g. 0.003 = 0.3%)
  option: number;      // Option brokerage per lot in ₹ (e.g. 100 = ₹100/lot)
  intraStock: number;  // Intraday brokerage rate (decimal, e.g. 0.0003 = 0.03%)
  future?: number;     // Futures brokerage rate (decimal, e.g. 0.0003 = 0.03%)
}

export interface CommissionRates {
  stock: number;        // percentage, default 0.30%
  intraday: number;     // percentage, default 0.03%
  futStk: number;       // percentage, default 0.03%
  option: number;       // per lot in INR, default 100
}

export const DEFAULT_COMMISSION_RATES: CommissionRates = {
  stock: 0.30,
  intraday: 0.03,
  futStk: 0.03,
  option: 100,
};

export interface Client {
  clientId: string;
  name: string;
  initialCapital: number;
  brokerageRates: BrokerageRates;
  optionBrokeragePerLot?: number;
  referredByEmployeeId?: string;
  referredByName?: string;
  commissionRates?: CommissionRates;
  currentDeployedAmount?: number;
  startOfDayAvailableCash?: number;
  startOfDayCashDate?: string;
  brokerClientCode?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ClientCreateInput {
  name: string;
  initialCapital: number;
  brokerageRates: BrokerageRates;
  optionBrokeragePerLot?: number;
  referredByEmployeeId?: string;
  referredByName?: string;
  commissionRates?: Partial<CommissionRates>;
  brokerClientCode?: string;
}

// ============================================================================
// TRADE TYPES
// ============================================================================

export interface Trade {
  tradeId: string;
  clientId: string;
  clientName: string;
  serialNumber: number;
  tradeType: TradeType;
  date: string;
  saleDate?: string;
  direction?: 'BUY' | 'SELL';
  exchange?: 'NSE' | 'BSE';
  isMTF?: boolean;
  leverage?: string;
  stopLoss?: number;
  scrip: string;
  purchaseLots: number;
  lotSize: number;
  purchasePrice: number;
  saleLots: number;
  salePrice: number;
  balanceLots: number;
  status: TradeStatus | '';
  investedAmount: number;
  saleValue: number;
  realizedPnL: number;
  unrealizedPnL: number;
  totalPnL: number;
  combinedPnL: number;
  pnlPercentage: number;
  currentPrice: number;
  currentValue: number;
  brokerageAmount: number;
  estimatedOpenBrokerage: number;
  deployedAmount?: number;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  isDeleted: boolean;
}

export interface TradeCreateInput {
  clientId: string;
  tradeType: TradeType;
  date: string;
  saleDate?: string;
  direction?: 'BUY' | 'SELL';
  exchange?: 'NSE' | 'BSE';
  isMTF?: boolean;
  leverage?: string;
  stopLoss?: number;
  scrip: string;
  purchaseLots: number;
  lotSize: number;
  purchasePrice: number;
  saleLots?: number;
  salePrice?: number;
  currentPrice?: number;
}

export interface TradeFilters {
  clientId?: string;
  status?: TradeStatus | '';
  tradeType?: TradeType;
  scrip?: string;
  startDate?: string;
  endDate?: string;
}

export interface AddSaleInput {
  saleLots: number;
  salePrice: number;
}

// ============================================================================
// PAYOUT TYPES
// ============================================================================

export interface Payout {
  payoutId: string;
  clientId: string;
  clientName: string;
  amount: number;
  date: string;
  lossCarryForward: number;
  notes: string;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
}

export interface PayoutCreateInput {
  clientId: string;
  amount: number;
  date: string;
  lossCarryForward?: number;
  notes?: string;
}

// ============================================================================
// RABBIT SCORE TYPES
// ============================================================================

export interface DealCounts {
  positive: number;
  negative: number;
  range: number;
  total: number;
}

export interface RabbitScoreResult {
  score: number;
  deals: DealCounts;
  interpretation: string;
  colorCode: 'green' | 'yellow' | 'red';
}

export interface ClientRabbitScore {
  clientId: string;
  clientName: string;
  rabbitScore: RabbitScoreResult;
}

// ============================================================================
// PORTFOLIO TYPES
// ============================================================================

export interface PortfolioSummary {
  clientId: string;
  clientName: string;
  initialCapital: number;
  totalInvested: number;
  currentValue: number;
  totalRealizedPnL: number;
  totalUnrealizedPnL: number;
  totalPnL: number;
  pnlPercentage: number;
  availableCash: number;
  totalPayouts: number;
  lossCarryForward: number;
  openPositionsCount: number;
  closedTradesCount: number;
  rabbitScore: RabbitScoreResult;
  dealCounts: DealCounts;
}

// ============================================================================
// DASHBOARD TYPES
// ============================================================================

export interface DashboardSummary {
  totalClients: number;
  activeClients: number;
  totalInvested: number;
  totalCurrentValue: number;
  totalPnL: number;
  totalPnLPercentage: number;
  totalOpenPositions: number;
  totalClosedTrades: number;
  portfolioRabbitScore: RabbitScoreResult;
  totalRevenue: number;
  totalTurnover: number;
  totalOptionLots: number;
  tradesByType: { equity: number; future: number; option: number };
  openTradesByType: { equity: number; future: number; option: number };
  clientSummaries: ClientSummary[];
  alerts: Alert[];
}

export interface ClientSummary {
  clientId: string;
  clientName: string;
  totalInvested: number;
  currentValue: number;
  totalPnL: number;
  pnlPercentage: number;
  rabbitScore: number;
  openPositions: number;
  totalBrokerage: number;
  totalTurnover: number;
  status: 'healthy' | 'warning' | 'critical';
}

export interface ChargesBreakdown {
  brokerage: number;
  stt: number;
  exchangeCharges: number;
  sebiCharges: number;
  stampDuty: number;
  gst: number;
  totalCharges: number;
}

export interface Alert {
  alertId: string;
  type: 'pnl' | 'rabbit_score' | 'position' | 'risk';
  severity: AlertSeverity;
  clientId?: string;
  clientName?: string;
  message: string;
  value: number;
  threshold: number;
  timestamp: string;
}

// ============================================================================
// RISK TYPES
// ============================================================================

export interface RiskMetrics {
  overallRiskScore: number;
  rabbitScoreTrend: 'improving' | 'stable' | 'declining';
  highRiskPositions: HighRiskPosition[];
  alertsSummary: AlertsSummary;
  clientRiskMatrix: ClientRiskItem[];
}

export interface HighRiskPosition {
  tradeId: string;
  clientId: string;
  clientName: string;
  scrip: string;
  investedAmount: number;
  currentValue: number;
  pnlPercentage: number;
  daysHeld: number;
}

export interface AlertsSummary {
  critical: number;
  high: number;
  medium: number;
  low: number;
  total: number;
}

export interface ClientRiskItem {
  clientId: string;
  clientName: string;
  riskScore: number;
  riskLevel: 'low' | 'medium' | 'high' | 'critical';
  factors: string[];
}

// ============================================================================
// ANALYTICS TYPES
// ============================================================================

export interface PerformanceTrend {
  date: string;
  totalPnL: number;
  rabbitScore: number;
  totalInvested: number;
  currentValue: number;
}

export interface ScripPerformance {
  scrip: string;
  totalTrades: number;
  totalPnL: number;
  avgPnLPercentage: number;
  winRate: number;
}

export interface DealDistribution {
  name: string;
  value: number;
  color: string;
}

// ============================================================================
// API RESPONSE TYPES
// ============================================================================

export interface ApiResponse<T = unknown> {
  success: boolean;
  data?: T;
  error?: ApiError;
  pagination?: PaginationInfo;
}

export interface ApiError {
  code: string;
  message: string;
  details?: Record<string, unknown>;
}

export interface PaginationInfo {
  total: number;
  limit: number;
  offset: number;
  hasMore: boolean;
  lastKey?: string;
}

// ============================================================================
// EMPLOYEE & SALARY TYPES (HR/Payroll Module)
// ============================================================================

export enum AttendanceStatus {
  PRESENT = 'present',
  ABSENT = 'absent',
  HALF_DAY = 'half_day',
  LEAVE = 'leave',
  HOLIDAY = 'holiday',
}

export enum SalarySlipStatus {
  DRAFT = 'draft',
  GENERATED = 'generated',
  PENDING_APPROVAL = 'pending_approval',
  SENT = 'sent',
  PAID = 'paid',
}

export interface SalaryComponents {
  basic: number;
  hra: number;
  da: number;                  // Legacy/optional — defaults 0 for new employees
  specialAllowance: number;
  bonus?: number;              // New in HR revamp
  otherAllowances: number;
}

export interface SalaryDeductions {
  pf: number;
  professionalTax: number;
  incomeTax: number;           // Canonical (renamed from tds)
  tds: number;                 // Deprecated alias — dual-written for back-compat
  payrollGiving?: number;      // New in HR revamp
  mediclaim?: number;          // New in HR revamp
  otherDeductions: number;
}

export interface DeductionOverrides {
  professionalTax?: number;
  incomeTax?: number;
  payrollGiving?: number;
  mediclaim?: number;
  otherDeductions?: number;
}

/**
 * Frozen snapshot of employee details captured at slip generation time.
 * Decouples a stored slip from later edits to the underlying Employee record.
 */
export interface EmployeeSnapshot {
  employeeCode?: string;
  grade?: string;
  uanNumber?: string;
  esicNumber?: string;
  dateOfBirth?: string;
  panNumber?: string;
  pfNumber?: string;
  joiningDate?: string;
  bankName?: string;
  bankAccountNo?: string;
  ifscCode?: string;
}

export interface Employee {
  employeeId: string;
  name: string;
  email: string;
  phone?: string;
  designation: string;
  department?: string;
  joiningDate: string;
  bankName?: string;
  bankAccountNo?: string;
  ifscCode?: string;
  panNumber?: string;
  pfNumber?: string;
  // Kotak-style payslip fields (new in HR revamp)
  employeeCode?: string;
  grade?: string;
  uanNumber?: string;
  esicNumber?: string;
  dateOfBirth?: string;
  baseSalary: number;
  salaryComponents: SalaryComponents;
  deductionOverrides?: DeductionOverrides;
  systemRole?: UserRole;  // Role for system access when employee signs up
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
}

export interface EmployeeCreateInput {
  name: string;
  email: string;
  phone?: string;
  designation: string;
  department?: string;
  joiningDate: string;
  bankName?: string;
  bankAccountNo?: string;
  ifscCode?: string;
  panNumber?: string;
  pfNumber?: string;
  // Kotak-style payslip fields
  employeeCode?: string;
  grade?: string;
  uanNumber?: string;
  esicNumber?: string;
  dateOfBirth?: string;
  baseSalary: number;
  salaryComponents?: Partial<SalaryComponents>;
  deductionOverrides?: DeductionOverrides;
  systemRole?: UserRole;  // Role for system access when employee signs up
  sendWelcomeEmail?: boolean;  // Create-time toggle, not persisted
}

export interface EmployeeUpdateInput {
  name?: string;
  email?: string;
  phone?: string;
  designation?: string;
  department?: string;
  joiningDate?: string;
  bankName?: string;
  bankAccountNo?: string;
  ifscCode?: string;
  panNumber?: string;
  pfNumber?: string;
  // Kotak-style payslip fields
  employeeCode?: string;
  grade?: string;
  uanNumber?: string;
  esicNumber?: string;
  dateOfBirth?: string;
  baseSalary?: number;
  salaryComponents?: Partial<SalaryComponents>;
  deductionOverrides?: DeductionOverrides;
  isActive?: boolean;
  systemRole?: UserRole;
}

export interface Attendance {
  attendanceId: string;
  employeeId: string;
  employeeName: string;
  date: string;
  status: AttendanceStatus;
  checkInTime?: string;
  checkOutTime?: string;
  notes?: string;
  createdAt: string;
  updatedAt: string;
  markedBy: string;
}

export interface AttendanceCreateInput {
  employeeId: string;
  date: string;
  status: AttendanceStatus;
  checkInTime?: string;
  checkOutTime?: string;
  notes?: string;
}

export interface AttendanceBulkInput {
  date: string;
  records: {
    employeeId: string;
    status: AttendanceStatus;
    notes?: string;
  }[];
}

export interface AttendanceFilters {
  employeeId?: string;
  startDate?: string;
  endDate?: string;
  status?: AttendanceStatus;
  month?: string;
}

export interface SalarySlip {
  salarySlipId: string;
  employeeId: string;
  employeeName: string;
  employeeEmail: string;
  designation: string;
  department?: string;
  month: string;
  year: number;
  totalWorkingDays: number;
  daysPresent: number;
  daysAbsent: number;
  halfDays: number;
  leaves: number;
  effectiveWorkingDays: number;
  baseSalary: number;
  earnings: SalaryComponents;
  grossEarnings: number;
  deductions: SalaryDeductions;
  totalDeductions: number;
  commission?: number;
  commissionDetails?: {
    clientId: string;
    clientName: string;
    tradeType: string;
    totalValue: number;
    commissionAmount: number;
  }[];
  netPay: number;
  bankName?: string;
  bankAccountNo?: string;
  ifscCode?: string;
  // Kotak-style payslip fields (new in HR revamp)
  employeeSnapshot?: EmployeeSnapshot;
  lwp?: number;
  arrearsDays?: number;
  status: SalarySlipStatus;
  generatedAt?: string;
  approvedAt?: string;
  approvedBy?: string;
  sentAt?: string;
  paidAt?: string;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  notes?: string;
}

export interface SalarySlipCreateInput {
  employeeId: string;
  month: string;
  year: number;
  totalWorkingDays?: number;
  notes?: string;
  /**
   * Manual override — when supplied, slip is generated without consulting
   * attendance. Used by HR when attendance was never marked. `daysAbsent: 0`
   * means full salary.
   */
  manualAttendance?: {
    daysAbsent: number;
    halfDays?: number;
    leaves?: number;
  };
}

export interface SalarySlipFilters {
  employeeId?: string;
  month?: string;
  year?: number;
  status?: SalarySlipStatus;
}

export interface AttendanceSummary {
  present: number;
  absent: number;
  halfDays: number;
  leaves: number;
  holidays: number;
  total: number;
  effectiveWorkingDays: number;
}

export interface PayrollDashboard {
  totalEmployees: number;
  totalBaseSalary: number;
  currentMonth: string;
  currentMonthPayroll: {
    slipsGenerated: number;
    pendingSlips: number;
    totalGross: number;
    totalDeductions: number;
    totalNetPay: number;
    slipsSent: number;
    slipsPaid: number;
  };
}

// ============================================================================
// FEATURE FLAGS TYPES (Role-Based Feature Toggle System)
// ============================================================================

// Features that can be toggled per role
export interface RoleFeatures {
  dashboard: boolean;
  trades: boolean;
  clients: boolean;
  riskMonitor: boolean;
  payouts: boolean;
  analytics: boolean;
}

// Feature names type for type safety
export type FeatureName = keyof RoleFeatures;

// Role names that can have feature toggles (excludes super_admin who has all access)
export type ToggleableRole = 'admin' | 'hr' | 'employee';

export interface FeatureFlags {
  featureFlagsId: string;
  // Per-role feature settings
  admin: RoleFeatures;
  hr: RoleFeatures;
  employee: RoleFeatures;
  // Metadata
  updatedAt: string;
  updatedBy: string;
}

export interface FeatureFlagsUpdateInput {
  // Can update entire role's features
  admin?: Partial<RoleFeatures>;
  hr?: Partial<RoleFeatures>;
  employee?: Partial<RoleFeatures>;
  // Or enable/disable all features for a role
  enableAllForRole?: ToggleableRole;
  disableAllForRole?: ToggleableRole;
  // Master toggles for all roles
  enableAllFeatures?: boolean;
  disableAllFeatures?: boolean;
}

// Default features - all disabled for safety
export const DEFAULT_ROLE_FEATURES: RoleFeatures = {
  dashboard: false,
  trades: false,
  clients: false,
  riskMonitor: false,
  payouts: false,
  analytics: false,
};
